import React, { useState, useEffect } from 'react';
import { reportService } from '../../services/reportService';
import { fleetService } from '../../services/fleetService';
import ReportExportOptions from '../../components/reports/ReportExportOptions';
import FleetComparisonChart from '../../components/reports/FleetComparisonChart';
import {
    Truck,
    TrendingUp,
    DollarSign,
    Users,
    ChevronDown
} from 'lucide-react';

const FleetComparisonReportsPage = () => {
    const [fleets, setFleets] = useState([]);
    const [selectedFleets, setSelectedFleets] = useState([]);
    const [comparisonData, setComparisonData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedMetric, setSelectedMetric] = useState('total_revenue');

    // 可比較的指標
    const metrics = [
        {
            value: 'total_revenue',
            label: '總收入',
            icon: DollarSign,
            description: '比較各車隊的總營收'
        },
        {
            value: 'total_orders',
            label: '訂單數量',
            icon: Truck,
            description: '比較各車隊的訂單總數'
        },
        {
            value: 'active_drivers',
            label: '活躍司機',
            icon: Users,
            description: '比較各車隊的活躍司機人數'
        }
    ];

    // 時間週期選項
    const periodOptions = [
        { value: 'daily', label: '每日' },
        { value: 'weekly', label: '每週' },
        { value: 'monthly', label: '每月' },
        { value: 'quarterly', label: '每季' }
    ];

    const [filters, setFilters] = useState({
        start_date: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        period: 'monthly'
    });

    useEffect(() => {
        fetchFleets();
    }, []);

    useEffect(() => {
        if (selectedFleets.length > 0) {
            fetchComparisonData();
        }
    }, [selectedFleets, selectedMetric, filters]);

    const fetchFleets = async () => {
        try {
            const fleetsData = await fleetService.getFleets();
            setFleets(fleetsData);
            // 默認選擇前3個車隊
            setSelectedFleets(fleetsData.slice(0, 3).map(fleet => fleet.fleet_id));
        } catch (error) {
            console.error('Error fetching fleets:', error);
        }
    };

    const fetchComparisonData = async () => {
        setIsLoading(true);
        try {
            const data = await reportService.getFleetComparison({
                fleet_ids: selectedFleets,
                metric: selectedMetric,
                start_date: filters.start_date,
                end_date: filters.end_date,
                period: filters.period
            });
            setComparisonData(data);
        } catch (error) {
            console.error('Error fetching fleet comparison:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFleetSelection = (fleetId) => {
        setSelectedFleets(prev =>
            prev.includes(fleetId)
                ? prev.filter(id => id !== fleetId)
                : [...prev, fleetId]
        );
    };

    const handleExportReport = (format) => {
        reportService.exportReport('fleets', {
            fleet_ids: selectedFleets,
            metric: selectedMetric,
            start_date: filters.start_date,
            end_date: filters.end_date,
            period: filters.period,
            format
        });
    };

    const renderMetricDetails = () => {
        const currentMetric = metrics.find(m => m.value === selectedMetric);
        return (
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center">
                    <currentMetric.icon className="w-6 h-6 mr-3 text-blue-500" />
                    <div>
                        <h3 className="font-bold">{currentMetric.label}</h3>
                        <p className="text-sm text-gray-600">{currentMetric.description}</p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">車隊比較報表</h1>
                <ReportExportOptions onExport={handleExportReport} />
            </div>

            {/* 篩選器 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            開始日期
                        </label>
                        <input
                            type="date"
                            value={filters.start_date}
                            onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                            className="w-full border border-gray-300 rounded-md p-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            結束日期
                        </label>
                        <input
                            type="date"
                            value={filters.end_date}
                            onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                            className="w-full border border-gray-300 rounded-md p-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            統計週期
                        </label>
                        <select
                            value={filters.period}
                            onChange={(e) => setFilters(prev => ({ ...prev, period: e.target.value }))}
                            className="w-full border border-gray-300 rounded-md p-2"
                        >
                            {periodOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            比較指標
                        </label>
                        <select
                            value={selectedMetric}
                            onChange={(e) => setSelectedMetric(e.target.value)}
                            className="w-full border border-gray-300 rounded-md p-2"
                        >
                            {metrics.map(metric => (
                                <option key={metric.value} value={metric.value}>
                                    {metric.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* 車隊選擇 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="font-bold mb-4">選擇比較車隊</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {fleets.map(fleet => (
                        <div
                            key={fleet.fleet_id}
                            className={`p-3 border rounded-lg cursor-pointer transition-all ${selectedFleets.includes(fleet.fleet_id)
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-300 hover:border-blue-300'
                                }`}
                            onClick={() => handleFleetSelection(fleet.fleet_id)}
                        >
                            <div className="flex items-center">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                    {fleet.fleet_name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <p className="font-medium">{fleet.fleet_name}</p>
                                    <p className="text-xs text-gray-500">ID: {fleet.fleet_id}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 指標說明 */}
            {renderMetricDetails()}

            {/* 圖表展示 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="font-bold mb-4">比較圖表</h3>
                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : comparisonData ? (
                    <FleetComparisonChart
                        data={comparisonData}
                        metric={selectedMetric}
                    />
                ) : (
                    <div className="text-center text-gray-500 h-64 flex items-center justify-center">
                        <p>請選擇車隊和指標以顯示比較圖表</p>
                    </div>
                )}
            </div>

            {/* 詳細數據表格 */}
            {comparisonData && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="font-bold mb-4">詳細數據</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="p-3 border text-left">車隊</th>
                                    {comparisonData.periods.map(period => (
                                        <th key={period} className="p-3 border text-right">
                                            {period}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {comparisonData.fleets.map(fleet => (
                                    <tr key={fleet.fleet_id}>
                                        <td className="p-3 border font-medium">{fleet.fleet_name}</td>
                                        {comparisonData.periods.map(period => (
                                            <td key={period} className="p-3 border text-right">
                                                {fleet.data[period] !== undefined
                                                    ? fleet.data[period].toLocaleString()
                                                    : '-'}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FleetComparisonReportsPage;